/**
 * My Account — name + delivery address editor.
 *
 * These are PROFILE changes, not identity changes: they never move
 * auth.users.email, so there is no OTP here. Adding a verification step to a
 * name correction would be friction without a threat model.
 *
 * THE ORDER-INTEGRITY RULE, stated in the UI as well as in code: the address on
 * a confirmed order is a SNAPSHOT held on card_requests, and the issuer ships
 * against that snapshot. Editing the profile changes the DEFAULT used to
 * prefill the NEXT order and nothing else. A confirmed order's address (or the
 * cardholder name embossed on it) can only be changed via support.
 *
 * The form itself is the SAME AddressFields group S6 uses — one address form in
 * the codebase, so validation, ZIP courtesy autofill and region handling cannot
 * drift between the order flow and My Account.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Field, GhostButton, PrimaryButton, inputClass } from "@/components/consumer/chrome";
import AddressFields from "@/components/consumer/AddressFields";
import {
  AddressDraft,
  EMPTY_ADDRESS,
  addressDraftFrom,
  addressMissingFields,
  canonicalAddress,
} from "@/lib/postal-address";
import { getOwnProfileDetails, updateProfileDetails } from "@/services/profiles";
import { postalAddressSummary } from "@/lib/repeat-flow";

interface Props {
  userId: string | null;
  email: string | null;
  /** Labels supplied by the host screen's content store. */
  labels: { nameLabel: string; emailLabel: string; addressLabel: string };
  /** Fallback display values from the latest order, used until a profile exists. */
  fallbackName: string | null;
  fallbackAddress: string | null;
  /** Lets the host refresh anything derived from the profile. */
  onSaved?: () => void;
}

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-slate-muted">
    {children}
  </span>
);

export default function ProfileDetailsEditor({
  userId,
  email,
  labels,
  fallbackName,
  fallbackAddress,
  onSaved,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [address, setAddress] = useState<AddressDraft>(EMPTY_ADDRESS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  // What the profile itself holds, once known. Until then the summary shows the
  // latest order's values so the screen is never blank.
  const [savedName, setSavedName] = useState<string | null>(null);
  const [savedAddress, setSavedAddress] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const { data } = await getOwnProfileDetails(userId);
      if (cancelled) return;
      const p = data as any;
      setFirst(p?.first_name ?? "");
      setLast(p?.last_name ?? "");
      setAddress(addressDraftFrom(p?.postal_address ?? null));
      const nm = `${p?.first_name ?? ""} ${p?.last_name ?? ""}`.trim();
      setSavedName(nm || null);
      setSavedAddress(postalAddressSummary(p?.postal_address ?? null));
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const shownName = savedName ?? fallbackName;
  const shownAddress = savedAddress ?? fallbackAddress;

  function startEditing() {
    // Seed from the latest order if no profile row exists yet, so the customer
    // corrects a prefilled address rather than retyping it.
    if (loaded && !savedName && fallbackName) {
      const parts = fallbackName.split(/\s+/);
      if (!first) setFirst(parts[0] ?? "");
      if (!last) setLast(parts.slice(1).join(" "));
    }
    setError(null);
    setSavedNote(null);
    setEditing(true);
  }

  async function save() {
    setError(null);
    if (!first.trim() || !last.trim()) {
      setError("First and last name are both required.");
      return;
    }
    // A partially filled address is refused with the fields NAMED — same
    // contract as S6, never a bare "invalid".
    const touched = Object.values(address).some((v) => String(v ?? "").trim() !== "");
    const missing = touched ? addressMissingFields(address) : [];
    if (missing.length) {
      setError(`Still needed: ${missing.join(", ")}`);
      return;
    }
    setSaving(true);
    const { data, error: rpcError } = await updateProfileDetails({
      firstName: first,
      lastName: last,
      postalAddress: touched ? canonicalAddress(address) : null,
    });
    setSaving(false);
    if (rpcError) {
      setError("Couldn't save. Try again.");
      return;
    }
    const row = Array.isArray(data) ? (data[0] as any) : (data as any);
    const nm = `${row?.o_first_name ?? first} ${row?.o_last_name ?? last}`.trim();
    setSavedName(nm || null);
    setSavedAddress(postalAddressSummary(row?.o_postal_address ?? canonicalAddress(address)));
    setAddress(addressDraftFrom(row?.o_postal_address ?? canonicalAddress(address)));
    setEditing(false);
    setSavedNote("Saved");
    setTimeout(() => setSavedNote(null), 2500);
    onSaved?.();
  }

  if (!editing) {
    return (
      <div>
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          {shownName ? (
            <div className="flex flex-col gap-[3px]">
              <FieldLabel>{labels.nameLabel}</FieldLabel>
              <span className="text-[16px] font-medium" data-testid="s9-name">
                {shownName}
              </span>
            </div>
          ) : null}
          {email ? (
            <div className="flex flex-col gap-[3px]">
              <FieldLabel>{labels.emailLabel}</FieldLabel>
              <span className="text-[16px] font-medium" data-testid="s9-email">
                {email}
              </span>
            </div>
          ) : null}
          {shownAddress ? (
            <div className="flex flex-col gap-[3px]">
              <FieldLabel>{labels.addressLabel}</FieldLabel>
              <span className="text-[16px] font-medium" data-testid="s9-address">
                {shownAddress}
              </span>
            </div>
          ) : null}
        </div>
        <div className="mt-3.5 flex items-center gap-3">
          <button
            type="button"
            onClick={startEditing}
            disabled={!userId}
            className="min-h-11 rounded-xl border border-foreground/15 px-3.5 text-[14px] font-semibold text-foreground disabled:opacity-60"
            data-testid="s9-edit-details"
          >
            Edit name &amp; address
          </button>
          {savedNote ? (
            <span className="text-[14px] font-semibold text-[#1A6B42]">{savedNote}</span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3.5" data-testid="s9-details-editor">
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        <Field label="First name">
          <input
            value={first}
            onChange={(e) => setFirst(e.target.value)}
            className={inputClass}
            autoComplete="given-name"
            data-testid="s9-first-name"
          />
        </Field>
        <Field label="Last name">
          <input
            value={last}
            onChange={(e) => setLast(e.target.value)}
            className={inputClass}
            autoComplete="family-name"
            data-testid="s9-last-name"
          />
        </Field>
      </div>

      <AddressFields
        value={address}
        onChange={(patch) => setAddress((a) => ({ ...a, ...patch }))}
        testId="s9-address-fields"
        labels={{ title: "Default delivery address" }}
      />

      {/* The customer WILL assume this changes orders already placed. Say so. */}
      <div className="rounded-xl bg-foreground/[0.05] px-[15px] py-3 text-[14px] leading-[1.5] text-foreground/75">
        This updates the details we prefill on your next card. It won't change the name or
        address on cards you've already ordered — those are locked once the order is
        confirmed. Need one of those changed?{" "}
        <Link to="/support" className="font-semibold underline">
          Contact support
        </Link>
        .
      </div>

      {error ? (
        <p className="text-[14px] font-semibold text-[#C41112]" data-testid="s9-details-error">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2.5">
        <PrimaryButton onClick={save} disabled={saving} className="w-auto" data-testid="s9-save-details">
          {saving ? "Saving…" : "Save changes"}
        </PrimaryButton>
        <GhostButton onClick={() => setEditing(false)} className="w-auto">Cancel</GhostButton>
      </div>
    </div>
  );
}
